<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

const store = useStore();

onMounted(() => store.dispatch('campaigns/get'));

const resending = ref(null);

const handleResend = async campaign => {
  resending.value = campaign.id;
  try {
    await store.dispatch('campaigns/resend', campaign.id);
    useAlert('Reenvio concluído. Dados atualizados.');
  } catch {
    useAlert('Erro ao reenviar. Tente novamente.');
  } finally {
    resending.value = null;
  }
};

const campaigns = computed(() =>
  store.getters['campaigns/getWhatsAppCampaigns'].filter(
    c => c.campaign_status === 'completed'
  )
);

const expandedId = ref(null);
const activeTab = ref({});

const toggle = id => {
  expandedId.value = expandedId.value === id ? null : id;
  if (!activeTab.value[id]) activeTab.value[id] = 'sent';
};

const setTab = (id, tab) => {
  activeTab.value = { ...activeTab.value, [id]: tab };
};

const formatDate = iso => {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatScheduled = ts => {
  if (!ts) return '-';
  return new Date(ts * 1000).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const successRate = c => {
  const total = (c.sent_contacts?.length || 0) + (c.failed_contacts?.length || 0);
  if (!total) return null;
  return Math.round(((c.sent_contacts?.length || 0) / total) * 100);
};
</script>

<template>
  <div class="flex flex-col h-full overflow-y-auto">
    <!-- Header -->
    <div class="px-6 py-5 border-b border-n-weak">
      <h1 class="text-xl font-semibold text-n-slate-12">
        Relatório de Campanhas WhatsApp
      </h1>
      <p class="text-sm text-n-slate-11 mt-1">
        Histórico de envios e desempenho por campanha concluída.
      </p>
    </div>

    <!-- Vazio -->
    <div
      v-if="campaigns.length === 0"
      class="flex flex-col items-center justify-center flex-1 gap-2 text-n-slate-11"
    >
      <i class="i-lucide-inbox w-10 h-10 opacity-40" />
      <p class="text-sm">Nenhuma campanha concluída ainda.</p>
    </div>

    <!-- Tabela -->
    <div v-else class="px-6 py-4 flex flex-col gap-3">
      <!-- Linha de cada campanha -->
      <div
        v-for="campaign in campaigns"
        :key="campaign.id"
        class="rounded-xl border border-n-weak overflow-hidden"
      >
        <!-- Cabeçalho da linha -->
        <button
          type="button"
          class="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-n-alpha-1 transition-colors"
          @click="toggle(campaign.id)"
        >
          <!-- Nome + inbox -->
          <div class="flex-1 min-w-0">
            <p class="text-sm font-semibold text-n-slate-12 truncate">
              {{ campaign.title }}
            </p>
            <p class="text-xs text-n-slate-11 mt-0.5">
              {{ campaign.inbox?.name }} · {{ formatScheduled(campaign.scheduled_at) }}
            </p>
          </div>

          <!-- Stats -->
          <div class="flex items-center gap-4 shrink-0">
            <div class="text-center">
              <p class="text-base font-semibold text-n-teal-11">
                {{ campaign.sent_contacts?.length || 0 }}
              </p>
              <p class="text-xs text-n-slate-11">Enviados</p>
            </div>
            <div class="text-center">
              <p
                class="text-base font-semibold"
                :class="
                  (campaign.failed_contacts?.length || 0) > 0
                    ? 'text-n-ruby-9'
                    : 'text-n-slate-11'
                "
              >
                {{ campaign.failed_contacts?.length || 0 }}
              </p>
              <p class="text-xs text-n-slate-11">Falhas</p>
            </div>
            <div class="text-center w-12">
              <p class="text-base font-semibold text-n-slate-12">
                {{ successRate(campaign) !== null ? `${successRate(campaign)}%` : '-' }}
              </p>
              <p class="text-xs text-n-slate-11">Taxa</p>
            </div>
            <i
              class="w-4 h-4 text-n-slate-11 transition-transform"
              :class="[
                expandedId === campaign.id
                  ? 'i-lucide-chevron-up'
                  : 'i-lucide-chevron-down',
              ]"
            />
          </div>
        </button>

        <!-- Detalhe expandido -->
        <div v-if="expandedId === campaign.id" class="border-t border-n-weak">
          <!-- Tabs -->
          <div class="flex border-b border-n-weak px-5">
            <button
              type="button"
              class="px-4 py-2.5 text-xs font-medium border-b-2 transition-colors"
              :class="
                activeTab[campaign.id] !== 'failed'
                  ? 'border-n-brand text-n-brand'
                  : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
              "
              @click="setTab(campaign.id, 'sent')"
            >
              Enviados ({{ campaign.sent_contacts?.length || 0 }})
            </button>
            <button
              type="button"
              class="px-4 py-2.5 text-xs font-medium border-b-2 transition-colors"
              :class="
                activeTab[campaign.id] === 'failed'
                  ? 'border-n-ruby-9 text-n-ruby-9'
                  : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
              "
              @click="setTab(campaign.id, 'failed')"
            >
              Falhas ({{ campaign.failed_contacts?.length || 0 }})
            </button>
          </div>

          <!-- Lista de enviados -->
          <div
            v-if="activeTab[campaign.id] !== 'failed'"
            class="max-h-72 overflow-y-auto divide-y divide-n-weak"
          >
            <div
              v-if="!campaign.sent_contacts?.length"
              class="px-5 py-4 text-xs text-n-slate-11 text-center"
            >
              Nenhum contato enviado.
            </div>
            <div
              v-for="contact in campaign.sent_contacts"
              :key="contact.id"
              class="flex items-center justify-between px-5 py-3"
            >
              <div>
                <p class="text-sm font-medium text-n-slate-12">
                  {{ contact.name }}
                </p>
                <p class="text-xs text-n-slate-11">{{ contact.phone }}</p>
              </div>
              <span class="text-xs text-n-slate-11">
                {{ formatDate(contact.sent_at) }}
              </span>
            </div>
          </div>

          <!-- Lista de falhas -->
          <div v-else class="flex flex-col">
            <div
              v-if="campaign.failed_contacts?.length"
              class="flex justify-end px-5 py-2 border-b border-n-weak"
            >
              <button
                type="button"
                class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-n-ruby-3 text-n-ruby-9 hover:bg-n-ruby-4 disabled:opacity-50 transition-colors"
                :disabled="resending === campaign.id"
                @click="handleResend(campaign)"
              >
                <i
                  class="w-3.5 h-3.5"
                  :class="resending === campaign.id ? 'i-lucide-loader-circle animate-spin' : 'i-lucide-refresh-cw'"
                />
                {{ resending === campaign.id ? 'Reenviando...' : 'Reenviar falhas' }}
              </button>
            </div>
            <div class="max-h-72 overflow-y-auto divide-y divide-n-weak">
            <div
              v-if="!campaign.failed_contacts?.length"
              class="px-5 py-4 text-xs text-n-slate-11 text-center"
            >
              Nenhuma falha registrada.
            </div>
            <div
              v-for="contact in campaign.failed_contacts"
              :key="contact.id"
              class="flex items-center justify-between px-5 py-3"
            >
              <div>
                <p class="text-sm font-medium text-n-slate-12">
                  {{ contact.name }}
                </p>
                <p class="text-xs text-n-slate-11">{{ contact.phone }}</p>
              </div>
              <span class="text-xs text-n-ruby-9 text-right max-w-[14rem]">
                {{ contact.error }}
              </span>
            </div>
          </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
